import { useCallback, useEffect, useState } from "react";
import {
  ChoiceGroup,
  ComboBox,
  DefaultButton,
  IChoiceGroupOption,
  IComboBox,
  IComboBoxOption,
  ILabelStyles,
  IStackStyles,
  Icon,
  Label,
  Panel,
  PanelType,
  PrimaryButton,
  Stack,
  Text,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import { UseMutationResult } from "react-query";
import axios from "axios";

import { Messages } from "../common/Messages";
import { FluentPanelEvents, PanelMode, TransactionMessage } from "../types";
import { requestErrorToMessage } from "../helpers";
import { ConfirmDialog } from "../common/Confirm";
import { capitalize } from "utils";
import { ICredentialInfo, ICredentialType, IManagedIdentity } from "types/stac";

interface CredentialPanelProps {
  initialValue?: ICredentialInfo;
  mode?: PanelMode;
  resourceType: "credential";
  managedIdentities?: IManagedIdentity[];
  credentialTypes?: ICredentialType[];
  title: string;
  body: JSX.Element;
  mutation: UseMutationResult<any, unknown, any, unknown>;
  onDismiss: () => void;
}

export const CredentialPanel = ({
  initialValue,
  mode = "create",
  resourceType,
  managedIdentities,
  credentialTypes,
  title,
  body,
  mutation,
  onDismiss,
}: CredentialPanelProps) => {
  const [isDirtyConfirmVisible, setDirtyConfirmVisible] = useState(false);
  const [message, setMessage] = useState<TransactionMessage>();
  const defaultCredentialType = "SasToken";
  const [containerURL, setContainerURL] = useState<string>();
  const [sasToken, setSasToken] = useState<string>();
  const [managedIdentity, setManagedIdentity] = useState<string>();
  const [, setIsLoading] = useState<boolean>(false);
  const [credentialType, setCredentialType] =
    useState<string>(defaultCredentialType);
  const [comboBoxOptions, setComboBoxOptions] = useState<IComboBoxOption[]>([]);
  const [choiceGroupOptions, setChoiceGroupOptions] = useState<IChoiceGroupOption[]>(
    []
  );
  const [, setError] = useState<string | null>(null);

  const handleManagedIdentity = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
    index?: number,
    value?: string
  ) => {
    if (option) {
      setManagedIdentity(option.key.toString()); // Ensure option is not undefined
    } else if (value !== undefined) {
      setManagedIdentity(value); // Handle freeform input
    }
  };

  const handleCredentialTypeChoiceGroup = (
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    option?: IChoiceGroupOption
  ) => {
    if (option) {
      setCredentialType(option.key.toString()); // Ensure option is not undefined
      if (option.key.toString() === "SaSToken") {
        setManagedIdentity("");
      } else {
        setSasToken("");
      }
    } else {
      setCredentialType(defaultCredentialType);
    }
  };

  useEffect(() => {
    const FetchData = async () => {
      try {
        setIsLoading(true);
        const options: IComboBoxOption[] = (managedIdentities ?? []).map(
          (item: IManagedIdentity) => ({
            key: item.objectId, // Adjust according to the structure of ICredentialType
            text: item.resourceId, // Adjust according to the structure of ICredentialType
          })
        );
        setComboBoxOptions(options);
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    const FetchData = async () => {
      try {
        setIsLoading(true);
        const options: IChoiceGroupOption[] = (credentialTypes ?? []).map(
          (item: ICredentialType) => ({
            key: item.name,
            text: item.title,
          })
        );
        setChoiceGroupOptions(options);
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (mode === "update" && initialValue) {
      setCredentialType(initialValue.sourceType);
      setContainerURL(initialValue.connectionInfo.containerUrl);
      setSasToken(initialValue.connectionInfo.sasToken);
      setManagedIdentity(initialValue.connectionInfo.objectId);
    }
  }, [mode, initialValue]);

  const actionButtonText = capitalize(mode);

  useEffect(() => {
    if (mutation.isError && axios.isAxiosError(mutation.error)) {
      const errorMsg = requestErrorToMessage(mutation.error, resourceType);
      setMessage(errorMsg);
      mutation.reset();
    }
  }, [mutation.isError, mutation.error, resourceType, mutation]);

  useEffect(() => {
    if (mutation.isSuccess) {
      mutation.reset();
      onDismiss();
    }
  }, [mutation, mutation.isSuccess, onDismiss]);

  const isDirty = useCallback(() => {
    /*     if (editorRef.current?.getValue() !== formatStacBody(initialValue)) {
      return true;
    }
 */
    return false;
  }, []);

  const handleDismiss = useCallback(
    (ev?: FluentPanelEvents) => {
      if (ev) {
        ev.preventDefault();
        if (isDirty()) {
          setDirtyConfirmVisible(true);
        } else {
          onDismiss();
        }
      }
    },
    [isDirty, onDismiss]
  );

  const handleCreate = useCallback(() => {
    let credentialInput: ICredentialInfo = {
      id: initialValue?.id || "",
      sourceType: credentialType || "",
      created: "",
      connectionInfo: {
        containerUrl: containerURL || "",
        // sas token and managed identity are mutually exclusive so only one will be set
        ...(credentialType === "SasToken"
          ? { sasToken: sasToken || "" }
          : { objectId: managedIdentity || "" }),
      },
    };
    mutation.mutate(credentialInput);
  }, [
    mutation,
    credentialType,
    containerURL,
    sasToken,
    managedIdentity,
    initialValue,
  ]);

  const onRenderFooterContent = useCallback(
    () => (
      <div>
        <DefaultButton onClick={handleDismiss} styles={buttonStyles}>
          Cancel
        </DefaultButton>
        <PrimaryButton onClick={handleCreate} disabled={mutation.isLoading}>
          {actionButtonText}
        </PrimaryButton>
      </div>
    ),
    [mutation.isLoading, handleCreate, handleDismiss, actionButtonText]
  );

  const hideDialog = useCallback(() => setDirtyConfirmVisible(false), []);
  const hideDialogAndPanel = useCallback(() => {
    setDirtyConfirmVisible(false);
    onDismiss();
  }, [onDismiss]);

  const labelStyles: Partial<ILabelStyles> = {
    root: { padding: "13px 0px 5px" },
  };
  const stackStyles: Partial<IStackStyles> = { root: { minWidth: 650 } };
  const stackColumn1: Partial<IStackStyles> = { root: { minWidth: 150 } };
  const stackColumn2: Partial<IStackStyles> = {
    root: { flexGrow: 1, minWidth: 100 },
  };

  return (
    <>
      {isDirtyConfirmVisible && (
        <ConfirmDialog
          title="Discard Changes"
          onCancel={hideDialog}
          onConfirm={hideDialogAndPanel}
        >
          <Text>Are you sure you want to discard your changes?</Text>
        </ConfirmDialog>
      )}
      <Panel
        isOpen={true}
        type={PanelType.medium}
        closeButtonAriaLabel="Cancel"
        headerText={title}
        onDismiss={handleDismiss}
        isFooterAtBottom={true}
        onRenderFooterContent={onRenderFooterContent}
      >
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { height: "100%" } }}>
          {body}
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={stackStyles}>
            <Stack horizontal styles={stackColumn1}>
              <Label required styles={labelStyles}>
                Credentials Type
                <TooltipHost content="Choose the credentials type">
                  <Icon
                    iconName="Info"
                    style={{ marginLeft: 5, cursor: "pointer" }}
                  />
                </TooltipHost>
              </Label>
            </Stack>
            <Stack horizontal styles={stackColumn2}>
              <ChoiceGroup
                id="credentialType"
                defaultSelectedKey={credentialType}
                options={choiceGroupOptions}
                onChange={handleCredentialTypeChoiceGroup}
                aria-label="Credentials Type"
              />
            </Stack>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={stackStyles}>
            <Stack horizontal styles={stackColumn1}>
              <Label required styles={labelStyles}>
                Container URL
                <TooltipHost content="The container URL from your storage">
                  <Icon
                    iconName="Info"
                    style={{ marginLeft: 5, cursor: "pointer" }}
                  />
                </TooltipHost>
              </Label>
            </Stack>
            <Stack horizontal styles={stackColumn2}>
              <TextField
                id="containerURL"
                value={containerURL}
                styles={{ root: { width: "80%" } }}
                onChange={(e, newValue) => setContainerURL(newValue || "")}
                ariaLabel="Container URL"
              />
            </Stack>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={stackStyles}>
            <Stack horizontal styles={stackColumn1}>
              <Label required styles={labelStyles}>
                Credential Token
                <TooltipHost content="The Credential Token to access your storage">
                  <Icon
                    iconName="Info"
                    style={{ marginLeft: 5, cursor: "pointer" }}
                  />
                </TooltipHost>
              </Label>
            </Stack>
            <Stack horizontal styles={stackColumn2}>
              <TextField
                id="sasToken"
                value={sasToken}
                styles={{ root: { width: "80%" } }}
                onChange={(e, newValue) => setSasToken(newValue || "")}
                disabled={credentialType !== "SasToken"}
                ariaLabel="Credential Token"
              />
            </Stack>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={stackStyles}>
            <Stack horizontal styles={stackColumn1}>
              <Label required styles={labelStyles}>
                Managed Identity
                <TooltipHost content="Choose the Managed Identity to access your storage">
                  <Icon
                    iconName="Info"
                    style={{ marginLeft: 5, cursor: "pointer" }}
                  />
                </TooltipHost>
              </Label>
            </Stack>
            <Stack horizontal styles={stackColumn2}>
              <ComboBox
                id="managedIdentity"
                styles={{ root: { width: "190%" } }}
                selectedKey={managedIdentity}
                options={comboBoxOptions}
                onChange={handleManagedIdentity}
                //disabled is credentialType is SasToken
                disabled={credentialType === "SasToken"}
                ariaLabel="Managed Identity"
              ></ComboBox>
            </Stack>
          </Stack>
          {/* TODO custom template */}
          <Messages message={message} />
        </Stack>
      </Panel>
    </>
  );
};

const buttonStyles = { root: { marginRight: 8 } };
